import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TablePagination,
  Stack,
  Skeleton,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Link2,
  PlayCircle,
  SlidersHorizontal,
  X,
  ChevronLeft,
  Clock,
  Copy,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "../../components/Card";
import { Button } from "../../components/Button";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { CustomTextField } from "../../components/CustomTextField";
import { UnifiedModal } from "../../components/UnifiedModal";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { buildDateRangeChipLabel } from "../../utils/dateUtils";

const videoCategories = [
  { id: "masterclass", title: "MasterClass", filterValue: "MasterClass" },
  { id: "otr", title: "OTR", filterValue: "OTR" },
  {
    id: "asistencias",
    title: "Asistencias Técnicas de mentores",
    filterValue: "Asistencias Técnicas de mentores",
  },
  { id: "kitdigital", title: "KitDigital", filterValue: "Kit Digital" },
  {
    id: "partners",
    title: "Presentaciones de Partners",
    filterValue: "Presentaciones Partners",
  },
  {
    id: "radiografias",
    title: "Radiografías Digitales",
    filterValue: "Radiografía Digital",
  },
  { id: "preventas", title: "Preventas", filterValue: "Preventas" },
  {
    id: "cafe_ole",
    title: "Café Olé",
    filterValue: "Café Olé",
  },
];

const categoryFilterValueMap = videoCategories.reduce((acc, category) => {
  acc[category.id] = category.filterValue || category.title;
  return acc;
}, {});

const categoryLabelMap = videoCategories.reduce((acc, category) => {
  acc[category.id] = category.title;
  return acc;
}, {});

const categoryLookupByValue = videoCategories.reduce((acc, category) => {
  const options = [category.id, category.title, category.filterValue];
  options.forEach((value) => {
    if (!value) return;
    acc[value.toString().trim().toLowerCase()] = category.id;
  });
  return acc;
}, {});

const sortOptions = [
  { value: "-fecha_subida", label: "Fecha subida (más recientes)" },
  { value: "fecha_subida", label: "Fecha subida (más antiguas)" },
  { value: "name", label: "Nombre (A-Z)" },
];

const pageSizeOptions = [
  { value: 5, label: "5 vídeos" },
  { value: 10, label: "10 vídeos" },
  { value: 20, label: "20 vídeos" },
  { value: 50, label: "50 vídeos" },
];

const formatDate = (dateString) => {
  if (!dateString) return "Sin fecha";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getYoutubeThumbnail = (url) => {
  if (!url) return null;
  const regex = /(?:v=|\/)([0-9A-Za-z_-]{11})(?:\?|&|$)/;
  const match = url.match(regex);
  if (!match || !match[1]) {
    return null;
  }
  return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
};

const getInitials = (title) => {
  if (!title) return "?";
  return title
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

const copyTextToClipboard = async (text) => {
  if (!text) return false;

  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    if (typeof document !== "undefined") {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      return copied;
    }
  } catch (error) {
    console.error("Error copying link:", error);
  }

  return false;
};

function CategoryListItem({ category, isSelected, onClick }) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 16px",
        cursor: "pointer",
        transition: "all 0.2s",
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRight: isSelected
          ? `2px solid ${theme.palette.primary.main}`
          : "none",
        backgroundColor: isSelected
          ? theme.palette.action.selected
          : "transparent",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      {/* Play icon */}
      <PlayCircle
        size={20}
        color={
          isSelected ? theme.palette.primary.main : theme.palette.grey[400]
        }
        style={{ flexShrink: 0 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isSelected ? 600 : 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: isSelected
              ? theme.palette.primary.main
              : theme.palette.text.primary,
          }}
        >
          {category.title}
        </Typography>
      </Box>
    </Box>
  );
}

CategoryListItem.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Helper for filter chip with remove button
const FilterChip = ({ label, onRemove, theme }) => (
  <Box
    sx={{
      position: "relative",
      borderRadius: "6px",
      backgroundColor: `${theme.palette.primary.main}14`,
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: "0.8125rem",
      px: 1.5,
      py: 0.5,
      minHeight: "32px",
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      transition: "all 0.2s ease",
      "&:hover .chip-remove": {
        opacity: 1,
        transform: "scale(1)",
      },
    }}
  >
    {label}
    <IconButton
      size="small"
      className="chip-remove"
      onClick={onRemove}
      sx={{
        position: "absolute",
        top: "-6px",
        right: "-6px",
        width: 18,
        height: 18,
        p: 0,
        opacity: 0,
        transform: "scale(0.8)",
        transition: "all 0.2s ease",
        color: "white",
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
          transform: "scale(1.1)",
        },
      }}
    >
      <X size={12} />
    </IconButton>
  </Box>
);

function VideoCard({ video, onSelect }) {
  const theme = useTheme();
  const [copiedLink, setCopiedLink] = useState(null);
  const youtubeThumbnail = getYoutubeThumbnail(video?.enlace_youtube);
  const categories = Array.isArray(video?.categoria) ? video.categoria : [];
  const agencies = Array.isArray(video?.agencias_asociadas)
    ? video.agencias_asociadas
    : [];

  const links = [
    { label: "YouTube", url: video?.enlace_youtube },
    { label: "Backup Drive", url: video?.enlace_backup_drive },
    { label: "Video adicional", url: video?.video_2 },
    { label: "Helpdesk", url: video?.enlace_heldesk },
    { label: "Resumen IA", url: video?.enlace_sumario_video_ia },
  ].filter((link) => Boolean(link.url));

  const handleCopyLink = async (event, url) => {
    event.preventDefault();
    event.stopPropagation();

    const copied = await copyTextToClipboard(url);
    if (!copied) return;

    setCopiedLink(url);
    window.setTimeout(() => {
      setCopiedLink((currentValue) => (currentValue === url ? null : currentValue));
    }, 1800);
  };

  return (
    <Card
      onClick={onSelect}
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 250ms ease",
        "&:hover": {
          boxShadow: "0 6px 24px rgba(15, 23, 42, 0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          pt: "56.25%",
          backgroundColor: "#0f172a",
        }}
      >
        {youtubeThumbnail ? (
          <Box
            component="img"
            src={youtubeThumbnail}
            alt={video?.name || "Miniatura de vídeo"}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlayCircle size={36} color={theme.palette.primary.contrastText} />
          </Box>
        )}
      </Box>

      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: "100%",
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "#0f172a",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {video?.name || "Vídeo sin título"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "#475569",
              backgroundColor: "#eef2ff",
              px: 1,
              py: 0.25,
              borderRadius: "6px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {formatDate(video?.fecha_subida)}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "0.92rem",
            color: video?.sumario ? "#475569" : "#94a3b8",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontStyle: video?.sumario ? "normal" : "italic",
          }}
        >
          {video?.sumario || "Sin descripción"}
        </Typography>

        <Box
          sx={{
            mt: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {links.length > 0 ? (
            links.map((link) => (
              <Box
                key={link.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Box
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#0b63f4",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    wordBreak: "break-all",
                    minWidth: 0,
                    flex: 1,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Link2 size={16} color="#0b0b0b" />
                  {link.url}
                </Box>
                <Tooltip
                  title={copiedLink === link.url ? "Copiado" : "Copiar enlace"}
                  arrow
                >
                  <IconButton
                    size="small"
                    className="copy-link-button"
                    onClick={(event) => handleCopyLink(event, link.url)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      color:
                        copiedLink === link.url
                          ? theme.palette.success.main
                          : theme.palette.text.secondary,
                      backgroundColor:
                        copiedLink === link.url
                          ? theme.palette.success.light + "22"
                          : theme.palette.action.hover,
                      opacity: 1,
                      transform: "translateX(0)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor:
                          copiedLink === link.url
                            ? theme.palette.success.light + "33"
                            : theme.palette.action.selected,
                      },
                    }}
                  >
                    {copiedLink === link.url ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
              Sin enlaces públicos
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

VideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    categoria: PropTypes.arrayOf(PropTypes.string),
    fecha_subida: PropTypes.string,
    enlace_youtube: PropTypes.string,
    enlace_backup_drive: PropTypes.string,
    sumario: PropTypes.string,
    agencias_asociadas: PropTypes.arrayOf(PropTypes.string),
    enlace_heldesk: PropTypes.string,
    video_2: PropTypes.string,
    enlace_sumario_video_ia: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

function VideoCardSkeleton() {
  return (
    <Card
      sx={{
        border: "1px solid #e2e8f0",
        boxShadow: "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          pt: "56.25%",
          backgroundColor: "#f1f5f9",
        }}
      >
        <Skeleton
          variant="rectangular"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        />
      </Box>

      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: "100%",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width={80} height={20} />
        </Box>
        <Skeleton variant="text" width="90%" height={18} />
        <Skeleton variant="text" width="80%" height={18} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Skeleton variant="rounded" width={100} height={18} />
          <Skeleton variant="rounded" width={120} height={18} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export function Videos() {
  const theme = useTheme();
  const { setPageContext } = useOutletContext();
  const navigate = useNavigate();
  const { user: currentUser, interceptorReady } = useAuth();
  const [videos, setVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(["otr"]);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [sort, setSort] = useState("-fecha_subida");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [cursors, setCursors] = useState({ 0: null }); // Map page -> start_cursor
  const [totalCount, setTotalCount] = useState(-1); // Unknown total count initially

  const [apiError, setApiError] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    agency: null,
    sort: null,
    dateFrom: "",
    dateTo: "",
  });

  // Fetch agencies from API for superadmin
  const isSuperAdmin = currentUser?.role === "superadmin";
  const { data: agenciesData, isLoading: isLoadingAgencies } = getData(
    "/agencies/",
    interceptorReady && isSuperAdmin && isFilterModalOpen,
    [isFilterModalOpen]
  );

  useEffect(() => {
    // Reset page and cursors when filters change
    setPage(0);
    setCursors({ 0: null });
    setTotalCount(-1);
  }, [debouncedSearch, selectedCategories, selectedAgencies, dateRange, sort]);

  useEffect(() => {
    setPageContext({
      title: "Academia",
      subtitle: "Listado en vivo desde Notion (solo lectura)",
      showTitle: true,
    });
  }, [setPageContext]);

  const handleSearch = () => {
    setDebouncedSearch(searchQuery.trim());
  };

  const isDateRangeInvalid = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return false;
    return dateRange.from > dateRange.to;
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    setValidationError(
      isDateRangeInvalid
        ? "La fecha inicial no puede ser posterior a la final."
        : null
    );
  }, [isDateRangeInvalid]);

  const updateAvailableAgencies = useCallback((list) => {
    const agenciesSet = new Set();

    list.forEach((video) => {
      if (Array.isArray(video?.agencias_asociadas)) {
        video.agencias_asociadas.forEach((agency) => {
          if (agency) agenciesSet.add(agency);
        });
      }
    });

    setAvailableAgencies(
      Array.from(agenciesSet).sort((a, b) => a.localeCompare(b))
    );
  }, []);

  // Build the endpoint dynamically based on filters and pagination
  const videosEndpoint = useMemo(() => {
    if (isDateRangeInvalid) return null;

    const params = new URLSearchParams();
    params.set("page_size", pageSize.toString());

    // Get cursor for current page
    const currentCursor = cursors[page];
    if (currentCursor) {
      params.set("cursor", currentCursor);
    }

    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    }

    selectedCategories.forEach((categoryId) => {
      const filterValue = categoryFilterValueMap[categoryId];
      if (filterValue) {
        params.append("categoria", filterValue);
      }
    });

    // Agency filter disabled intentionally to avoid hiding videos due to
    // frontend-only filter state mismatches.

    if (dateRange.from) {
      params.set("fecha_subida_gte", dateRange.from);
    }

    if (dateRange.to) {
      params.set("fecha_subida_lte", dateRange.to);
    }

    if (sort) {
      params.set("sort", sort);
    }

    return `/knowledge/videos/?${params.toString()}`;
  }, [
    isDateRangeInvalid,
    pageSize,
    cursors,
    page,
    debouncedSearch,
    selectedCategories,
    dateRange.from,
    dateRange.to,
    sort,
  ]);

  // Use getData hook with caching
  const {
    data: videosResponse,
    isLoading,
    error: videosError,
  } = getData(videosEndpoint, interceptorReady && !!videosEndpoint, [
    videosEndpoint,
  ]);

  // Process the response when data changes
  useEffect(() => {
    if (!videosEndpoint) {
      setApiError(
        isDateRangeInvalid
          ? "La fecha inicial no puede ser posterior a la final."
          : null
      );
      return;
    }

    // Keep previous videos while loading to avoid flicker
    if (!videosResponse) {
      return;
    }

    const { results = [], next_cursor, has_more } = videosResponse || {};

    setVideos(() => {
      const nextList = results;
      updateAvailableAgencies(nextList);
      return nextList;
    });

    // Store next cursor for the NEXT page
    if (has_more && next_cursor) {
      setCursors((prev) => ({ ...prev, [page + 1]: next_cursor }));
    }

    // Try to estimate total count for TablePagination if possible, otherwise keep -1
    if (!has_more) {
      setTotalCount(page * pageSize + results.length);
    } else {
      setTotalCount(-1);
    }

    setApiError(null);
  }, [
    videosResponse,
    videosEndpoint,
    page,
    pageSize,
    updateAvailableAgencies,
    isDateRangeInvalid,
  ]);

  // Handle errors from getData
  useEffect(() => {
    if (!videosError) {
      return;
    }

    let message = "No pudimos recuperar los vídeos. Intenta nuevamente.";
    if (videosError?.response) {
      const { status, data } = videosError.response;
      const detail = data?.detail;
      switch (status) {
        case 400:
          message = detail || "Parámetros inválidos para la búsqueda.";
          break;
        case 401:
          message = "Tu sesión expiró. Inicia sesión nuevamente.";
          break;
        case 403:
          message = "No tienes permisos para consultar estos vídeos.";
          break;
        case 503:
          message = "Notion está temporalmente inestable. Prueba más tarde.";
          break;
        default:
          message = detail || message;
      }
    }
    setApiError(message);
  }, [videosError]);

  // Handlers for pagination
  const handleChangePage = (event, newPage) => {
    if (isLoading || newPage === page) return;
    if (newPage > page && !cursors[newPage]) return;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    setCursors({ 0: null });
  };

  const handleSidebarCategoryClick = (categoryId) => {
    // Don't trigger API call if clicking on already selected category
    if (
      selectedCategories.length === 1 &&
      selectedCategories[0] === categoryId
    ) {
      return;
    }
    // Always select a category - no deselection allowed
    setSelectedCategories([categoryId]);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategories(["otr"]); // Reset to default category
    setSelectedAgencies([]);
    setDateRange({ from: "", to: "" });
    setSort("-fecha_subida");
    setPageSize(20);
  };

  // Filter modal handlers
  const handleOpenFilterModal = () => {
    setDraftFilters({
      agency:
        selectedAgencies.length > 0
          ? { value: selectedAgencies[0], label: selectedAgencies[0] }
          : null,
      sort: {
        value: sort,
        label: sortOptions.find((o) => o.value === sort)?.label || sort,
      },
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
    });
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleDraftFilterChange = (field, value) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    if (draftFilters.agency?.value) {
      setSelectedAgencies([draftFilters.agency.value]);
    } else {
      setSelectedAgencies([]);
    }
    if (draftFilters.sort?.value) {
      setSort(draftFilters.sort.value);
    } else {
      setSort("-fecha_subida");
    }
    setDateRange({
      from: draftFilters.dateFrom || "",
      to: draftFilters.dateTo || "",
    });
    setIsFilterModalOpen(false);
  };

  const handleClearDraftFilters = () => {
    setDraftFilters({
      agency: null,
      sort: null,
      dateFrom: "",
      dateTo: "",
    });
  };

  const agencyOptions = useMemo(() => {
    // For superadmin, use agencies from API; otherwise use agencies from loaded videos
    if (
      isSuperAdmin &&
      Array.isArray(agenciesData) &&
      agenciesData.length > 0
    ) {
      return agenciesData.map((agency) => ({
        value: agency.id,
        label: agency.name,
      }));
    }
    return availableAgencies.map((agency) => ({
      value: agency,
      label: agency,
    }));
  }, [isSuperAdmin, agenciesData, availableAgencies]);

  const sortSelectOptions = useMemo(() => {
    return sortOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const pageSizeSelectOptions = useMemo(() => {
    return pageSizeOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const filterConfigs = useMemo(() => {
    const configs = [];

    // Only show agency filter for superadmins
    if (isSuperAdmin) {
      configs.push({
        key: "agency",
        title: "Agencia asociada",
        description: "Filtrar vídeos por agencia",
        options: agencyOptions,
        loading: isLoadingAgencies,
        placeholder: "Selecciona una agencia",
        includeAny: true,
      });
    }

    configs.push(
      {
        key: "sort",
        title: "Ordenar por",
        description: "Orden de los resultados",
        options: sortSelectOptions,
        placeholder: "Ordenar por...",
        includeAny: false,
        searchable: false,
      },
      {
        key: "date",
        type: "date-range",
        ui: "calendar-range",
        title: "Rango de fechas",
        description: "Filtrar por fecha de subida",
        startKey: "dateFrom",
        endKey: "dateTo",
      }
    );

    return configs;
  }, [agencyOptions, sortSelectOptions, isSuperAdmin, isLoadingAgencies]);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (debouncedSearch) {
      chips.push({
        key: `search-${debouncedSearch}`,
        label: `Búsqueda: ${debouncedSearch}`,
        onDelete: () => {
          setSearchQuery("");
          setDebouncedSearch("");
        },
      });
    }
    // Note: Categories are NOT shown as chips since they're visible in the left panel
    selectedAgencies.forEach((agency) => {
      chips.push({
        key: `agency-${agency}`,
        label: `Agencia: ${agency}`,
        onDelete: () =>
          setSelectedAgencies((prev) =>
            prev.filter((currentAgency) => currentAgency !== agency)
          ),
      });
    });
    if (dateRange.from || dateRange.to) {
      chips.push({
        key: `date-range-${dateRange.from || "start"}-${dateRange.to || "end"}`,
        label: buildDateRangeChipLabel({
          label: "Subida",
          start: dateRange.from,
          end: dateRange.to,
        }),
        onDelete: () => setDateRange({ from: "", to: "" }),
      });
    }
    if (sort && sort !== "-fecha_subida") {
      const sortLabel =
        sortOptions.find((option) => option.value === sort)?.label || sort;
      chips.push({
        key: `sort-${sort}`,
        label: `Orden: ${sortLabel}`,
        onDelete: () => setSort("-fecha_subida"),
      });
    }
    return chips;
  }, [debouncedSearch, selectedAgencies, dateRange.from, dateRange.to, sort]);

  const hasActiveFilters = activeFilters.length > 0;
  const totalPages = totalCount > -1 ? Math.ceil(totalCount / pageSize) : null;
  const canGoBack = page > 0 && !isLoading;
  const canGoNext =
    !isLoading &&
    (totalPages !== null ? page < totalPages - 1 : Boolean(cursors[page + 1]));

  const handleOpenVideoModal = (video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const videoGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(auto-fill, minmax(320px, 1fr))",
    },
    gap: 2,
    alignContent: "start",
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          height: "calc(100vh - 165px)",
          overflow: "hidden",
          flex: 1,
        }}
      >
        {/* Left Panel - Categories */}
        <Card
          sx={{
            width: 280,
            flexShrink: 0,
            padding: 0,
            height: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: "3px",
              "&:hover": { backgroundColor: theme.palette.text.secondary },
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.divider} transparent`,
          }}
        >
          <CardHeader sx={{ paddingBottom: "6px" }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Categorías
            </Typography>
          </CardHeader>
          <CardContent sx={{ padding: 0 }}>
            {videoCategories.map((category) => (
              <CategoryListItem
                key={category.id}
                category={category}
                isSelected={selectedCategories.includes(category.id)}
                onClick={() => handleSidebarCategoryClick(category.id)}
              />
            ))}
          </CardContent>
        </Card>

        {/* Right Panel - Outer white box wrapping everything */}
        <Card
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <CardHeader
            sx={{
              pb: 2,
              borderBottom: `1px solid ${theme.palette.divider} `,
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  flexShrink: 0,
                }}
              >
                {selectedCategories.length === 1
                  ? categoryLabelMap[selectedCategories[0]] ||
                  "Biblioteca de vídeos"
                  : "Biblioteca de vídeos"}
              </Typography>

              <Box
                sx={{
                  width: { xs: "100%", sm: "240px", md: "260px" },
                  flexShrink: 0,
                }}
              >
                <CustomTextField
                  placeholder="Buscar vídeos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  isSearch={true}
                  onSearchClick={handleSearch}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {activeFilters.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={chip.onDelete}
                    theme={theme}
                  />
                ))}

                <Tooltip title="Editar filtros" arrow>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenFilterModal}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      flexShrink: 0,
                      ml: "auto",
                      minWidth: "32px",
                      height: "32px",
                      padding: "4px",
                    }}
                    aria-label="Abrir filtros avanzados"
                  >
                    <SlidersHorizontal size={16} />
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </CardHeader>

          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: "100%",
              overflow: "hidden",
              pt: 0,
              pb: 0,
            }}
          >
            {apiError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {apiError}
              </Alert>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    p: 2,
                    pr: 1,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: theme.palette.grey[100],
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: theme.palette.grey[300],
                      borderRadius: "4px",
                    },
                  }}
                >
                  {isLoading ? (
                    <Box sx={videoGridSx}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <VideoCardSkeleton key={`skeleton - ${index} `} />
                      ))}
                    </Box>
                  ) : videos.length > 0 ? (
                    <Box sx={videoGridSx}>
                      {videos.map((video) => (
                        <VideoCard
                          key={video.id}
                          video={video}
                          onSelect={() => handleOpenVideoModal(video)}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        backgroundColor: "#f8fafc",
                        borderRadius: "12px",
                        border: "1px dashed #e2e8f0",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlayCircle
                        size={48}
                        color="#cbd5e1"
                        style={{ marginBottom: 12 }}
                      />
                      <Typography
                        sx={{ color: "#64748b", fontSize: "0.95rem" }}
                      >
                        No se han encontrado vídeos con los filtros actuales.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  backIconButtonProps={{ disabled: !canGoBack }}
                  nextIconButtonProps={{ disabled: !canGoNext }}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  labelRowsPerPage="Vídeos por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from} -${to} de ${count !== -1 ? count : `más de ${to}`
                    } `
                  }
                  sx={{
                    borderTop: `1px solid ${theme.palette.divider} `,
                    backgroundColor: theme.palette.background.paper,
                    "& .MuiTablePagination-toolbar": {
                      px: { xs: 1, md: 2 },
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: { xs: 1, md: 0 },
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      whiteSpace: { xs: "nowrap", md: "nowrap" },
                    },
                    "& .MuiTablePagination-select": {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Filter Modal */}
      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filtros de vídeos"
        filterConfigs={filterConfigs}
        draftFilters={draftFilters}
        onChange={handleDraftFilterChange}
        onApply={handleApplyFilters}
        onClearAll={handleClearDraftFilters}
      />

      <UnifiedModal
        open={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        title="Descripción detallada del vídeo"
        maxWidth="md"
      >
        <Stack spacing={2}>
          <Box
            sx={{
              maxHeight: "50vh",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[300],
                borderRadius: "3px",
              },
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: theme.palette.text.primary }}
            >
              {selectedVideo?.name || "Vídeo sin título"}
            </Typography>

            <Typography
              sx={{
                color: selectedVideo?.sumario
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                mt: 2,
                fontStyle: selectedVideo?.sumario ? "normal" : "italic",
              }}
            >
              {selectedVideo?.sumario || "Sin descripción"}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: theme.palette.divider,
            }}
          />

          {/* Fecha de publicación y enlace del vídeo */}
          <Stack
            direction="row"
            spacing={4}
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              py: 1,
            }}
          >
            {/* Enlace del vídeo */}
            {selectedVideo?.enlace_youtube && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Link2 size={16} color="#0b0b0b" />
                  Enlace del vídeo
                </Typography>
                <Typography
                  component="a"
                  href={selectedVideo.enlace_youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#0b63f4",
                    textDecoration: "none",
                    wordBreak: "break-all",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {selectedVideo.enlace_youtube}
                </Typography>
              </Box>
            )}
            {/* Fecha de publicación */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Clock size={16} />
                Fecha de publicación
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {selectedVideo?.fecha_subida
                  ? formatDate(selectedVideo.fecha_subida)
                  : "Sin fecha"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </UnifiedModal>
    </>
  );
}
